import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress, Grid, Dialog, DialogContent, DialogActions, Stack } from '@mui/material';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import Dropzone from '../../configured-components/form/configured-inputs/image-upload/Dropzone';
import { getFileURL } from '../../repository';
import useIsMobile from '../../hooks/useIsMobile';
import ImageLightbox from '../ImageLightbox';

const sx = {
    box: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    spinner: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
    },
    title: {
        marginBottom: '10px',
    },
    pdf: {
        borderRadius: 2,
        border: '1px solid #eaeaea',
        maxHeight: '500px',
        padding: 4,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 8,
    },
    pdfButton: {
        display: 'flex',
        flexDirection: 'column',
    },
    image: {
        maxHeight: '500px',
        flex: '1 1 auto',
        marginBottom: 8,
    },
    iframeWrapper: {
        position: 'relative',
        width: '100%',
        flex: '1 1 auto',
        display: 'flex',
    },
    iframeWrapperFill: {
        position: 'relative',
        width: '100%',
        height: '100%',
    },
    // Masks the Google Docs viewer "Pop out" / "Open in new window" button in
    // the top-right corner of the gview toolbar. The iframe is cross-origin,
    // so we can't hide it via CSS targeting the iframe's DOM.
    popOutMask: {
        position: 'absolute',
        top: 0,
        right: 0,
        width: 75,
        height: 75,
        backgroundColor: '#fff',
        pointerEvents: 'auto',
    },
};

const AttachmentUploaderWidget = ({ file, title, onSubmit, disabled = false, useExpress }) => {
    const [preview, setPreview] = useState();
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
    const isMobile = useIsMobile();

    const handleOpenLightbox = () => setLightboxOpen(true);
    const handleCloseLightbox = () => setLightboxOpen(false);
    const handleOpenPdfDialog = () => setPdfDialogOpen(true);
    const handleClosePdfDialog = () => setPdfDialogOpen(false);

    useEffect(() => {
        const getPreview = async () => {
            try {
                const url = await getFileURL(file, true, useExpress);
                setPreview(url);
            } catch (err) {
                // TODO: Set error appropriately
            }
        };

        if (file) {
            getPreview();
        }
    }, [file]);

    if (!file) {
        return (
            <Box padding={2}>
                <Typography sx={sx.marginBottom} variant="h6">
                    {title}
                </Typography>
                {!disabled ? (
                    <Dropzone
                        value={file}
                        preview={preview}
                        message={title.toLowerCase()}
                        handleDrop={onSubmit}
                        multiple={false}
                        // wrap
                    />
                ) : (
                    <Typography sx={sx.marginBottom} variant="h6">
                        No Image for {title}
                    </Typography>
                )}
            </Box>
        );
    }

    const isPdf = file?.includes('.pdf');
    if (!preview) {
        return (
            <Box padding={2} sx={sx.box}>
                <Typography sx={sx.marginBottom} variant="h6">
                    {title}
                </Typography>
                <Grid sx={sx.spinner}>
                    <CircularProgress size={50} color="secondary" disableShrink />
                </Grid>
            </Box>
        );
    }
    return (
        <Box padding={2} sx={sx.box}>
            <Typography sx={sx.marginBottom} variant="h6">
                {title}
            </Typography>
            {isPdf ? (
                <Box sx={sx.pdf}>
                    <Box sx={sx.iframeWrapper}>
                        <iframe
                            src={`https://docs.google.com/gview?url=${encodeURIComponent(preview)}&embedded=true`}
                            style={{
                                minWidth: '100%',
                                maxWidth: '100%',
                                minHeight: '400px',
                                maxHeight: '100%',
                                border: 'none',
                            }}
                            title="Slip"
                        />
                        {isMobile && <Box sx={sx.popOutMask} />}
                    </Box>
                    <Stack direction="row" spacing={2}>
                        {isMobile ? (
                            <Button sx={{ label: sx.pdfButton }} onClick={handleOpenPdfDialog}>
                                <PictureAsPdfIcon color="secondary" fontSize="large" />
                                View
                            </Button>
                        ) : (
                            <Button sx={{ label: sx.pdfButton }} onClick={() => window.open(preview, '_blank').focus()}>
                                <PictureAsPdfIcon color="secondary" fontSize="large" />
                                Download
                            </Button>
                        )}
                    </Stack>
                    {isMobile && (
                        <Dialog open={pdfDialogOpen} onClose={handleClosePdfDialog} fullWidth maxWidth="lg">
                            <DialogContent sx={{ padding: 0, height: '85vh' }}>
                                <Box sx={sx.iframeWrapperFill}>
                                    <iframe src={`https://docs.google.com/gview?url=${encodeURIComponent(preview)}&embedded=true`} style={{ width: '100%', height: '100%', border: 'none' }} title={title} />
                                    <Box sx={sx.popOutMask} />
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleClosePdfDialog} variant="outlined">
                                    Close
                                </Button>
                            </DialogActions>
                        </Dialog>
                    )}
                </Box>
            ) : (
                <>
                    <Box
                        component="img"
                        sx={sx.image}
                        alt={title}
                        src={preview}
                        onClick={handleOpenLightbox}
                        style={{ cursor: 'pointer' }}
                        // wrap
                    />
                    <ImageLightbox
                        imageUrl={preview}
                        open={lightboxOpen}
                        handleClose={handleCloseLightbox}
                        // wrap
                    />
                </>
            )}
        </Box>
    );
};

export default AttachmentUploaderWidget;
